import React, { useState, useEffect } from 'react';
import Slider from 'react-slick';
import { Link } from 'react-router-dom';

import { CustomNextArrow, CustomPrevArrow } from './CustomSliderArrows';
import { SliderNet } from './SliderNet';

interface EnterpriseSpecificationProps {
  subtitle: string;
  title: string;
  description: string;
  buttons: {
    buyNow?: {
      text: string;
      url: string;
    };
    moreInfo: {
      text: string;
      url: string;
    };
  };
  images: string[];
  inverted?: boolean;
}

export const EnterpriseSpecification: React.FC<EnterpriseSpecificationProps> = ({
  subtitle,
  title,
  description,
  buttons,
  images,
  inverted = false,
}) => {
  const [windowWidth, setWindowWidth] = useState(0);

  useEffect(() => {
    // Function to check and set the window width state
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    // Set initial state on component mount
    handleResize();

    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []); // Empty dependency array ensures this effect runs only once

  // Determine if dots or arrows should be shown based on the current window width
  const showDots =
    (windowWidth >= 992 && windowWidth <= 1200) || windowWidth <= 767;
  const showArrows =
    (windowWidth > 767 && windowWidth < 992) || windowWidth > 1200;

  const sliderSettings = {
    dots: showDots,
    arrows: showArrows,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    // Conditionally render arrows only when needed
    prevArrow: showArrows ? <CustomPrevArrow /> : undefined,
    nextArrow: showArrows ? <CustomNextArrow /> : undefined,
  };

  return (
    <div
      className={`enterprise-specification-section ${
        inverted ? 'enterprise-specification-section-inverted' : ''
      }`}
    >
      <div className="enterprise-specification-description-text">
        <div className="enterprise-specification-description-titles">
          <h2 className="enterprise-specification-description-subtitle">
            {subtitle}
          </h2>
          <h3 className="enterprise-specification-description-title">
            {title}
          </h3>
        </div>
        <div className="enterprise-specification-description">
          {description}
        </div>
        <div className="enterprise-specification-description-buttons">
          {buttons.buyNow && (
            <div className="dreamX__discover--button">
              <a
                href={buttons.buyNow.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn--primary btn--med enterprise-link-button"
                onMouseDown={event => event.preventDefault()}
              >
                {buttons.buyNow.text}
              </a>
            </div>
          )}
          <div className="dreamX__discover--button">
            <Link
              to={buttons.moreInfo.url}
              rel="noopener noreferrer"
              className="btn btn--primary btn--med enterprise-link-button"
              onMouseDown={event => event.preventDefault()}
            >
              {buttons.moreInfo.text}
            </Link>
          </div>
        </div>
      </div>
      <div className="enterprise-specification-slider">
        <SliderNet />
        <Slider {...sliderSettings}>
          {images.map((imageSrc, index) => (
            <div
              key={`slide-${index}`}
              className="image-slide-container"
              style={{ zIndex: 1000, width: '50%' }}
            >
              <img
                src={imageSrc}
                alt={`${title} - Slide Image Number: ${index + 1}`}
                style={{ objectFit: 'contain' }}
              />
            </div>
          ))}
        </Slider>
      </div>
    </div>
  );
};
